/**
 * Button Component - Enhanced with variants, sizes, and loading states
 */

import React from 'react';
import { Loader2 } from 'lucide-react';
import { colors, spacing, borderRadius, transitions } from '../../styles/design-tokens';

const variants = {
  primary: {
    base: 'bg-brand text-white hover:bg-brand-hover active:bg-brand-active',
    focus: 'focus:ring-2 focus:ring-brand focus:ring-offset-2',
    disabled: 'opacity-50 cursor-not-allowed',
  },
  secondary: {
    base: 'bg-white text-ink border border-neutral-200 hover:border-brand hover:text-brand',
    focus: 'focus:ring-2 focus:ring-brand focus:ring-offset-2',
    disabled: 'opacity-50 cursor-not-allowed',
  },
  outline: {
    base: 'bg-transparent text-brand border-2 border-brand hover:bg-brand-soft',
    focus: 'focus:ring-2 focus:ring-brand focus:ring-offset-2',
    disabled: 'opacity-50 cursor-not-allowed',
  },
  ghost: {
    base: 'bg-transparent text-brand hover:bg-brand-soft',
    focus: 'focus:ring-2 focus:ring-brand focus:ring-offset-2',
    disabled: 'opacity-50 cursor-not-allowed',
  },
  danger: {
    base: 'bg-error text-white hover:bg-error-dark active:bg-error-dark',
    focus: 'focus:ring-2 focus:ring-error focus:ring-offset-2',
    disabled: 'opacity-50 cursor-not-allowed',
  },
  success: {
    base: 'bg-success text-white hover:bg-success-dark active:bg-success-dark',
    focus: 'focus:ring-2 focus:ring-success focus:ring-offset-2',
    disabled: 'opacity-50 cursor-not-allowed',
  },
};

const sizes = {
  xs: 'px-2.5 py-1.5 text-xs',
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-sm',
  lg: 'px-8 py-4 text-base',
  xl: 'px-10 py-5 text-lg',
};

const Button = React.forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  disabled = false,
  loading = false,
  fullWidth = false,
  icon = null,
  iconPosition = 'left',
  onClick,
  type = 'button',
  as = 'button',
  ...props
}, ref) => {
  const variantStyles = variants[variant] || variants.primary;
  const sizeStyles = sizes[size] || sizes.md;

  const baseStyles = `
    inline-flex items-center justify-center
    font-semibold
    rounded-lg
    transition-all duration-200
    focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed
    ${fullWidth ? 'w-full' : ''}
    ${variantStyles.base}
    ${variantStyles.focus}
    ${disabled || loading ? variantStyles.disabled : ''}
    ${sizeStyles}
    ${className}
  `;

  const content = (
    <>
      {loading && <Loader2 size={16} className="mr-2 animate-spin shrink-0" />}
      {!loading && icon && iconPosition === 'left' && (
        <span className="mr-2 shrink-0">{icon}</span>
      )}
      {children}
      {!loading && icon && iconPosition === 'right' && (
        <span className="ml-2 shrink-0">{icon}</span>
      )}
    </>
  );

  if (as === 'a') {
    return (
      <a
        ref={ref}
        className={baseStyles}
        onClick={onClick}
        disabled={disabled || loading}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={baseStyles}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {content}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;